
import { Component, OnInit } from '@angular/core';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { filterDealData, filterLoanData } from '../../../types/resource';
import { TaskDataService } from '../../../shared/services/task-data.service';
import { IDealData } from '../../../modules/tasks/model/types';
import { UtilitiesService } from '../../../shared/components/framework/services/utilities.service';
import { FeatureFlagsService } from '../../../shared/services/feature-flags.service';

@Component({
  standalone: false,
  selector: 'app-loandata-base',
  template: ''
})
export class LoanDataBase implements OnInit {

  allLoanData: any[];
  allDealData: any[];
  allLoanNumbers: any[];
  activeLoanNumbers: any[];
  activeLoanData: any[];
  activeDealData: any[];
  loanData: any[];
  dealData: IDealData[];
  loanNumbers: any[];
  loanDataSubject$: BehaviorSubject<any[]> = new BehaviorSubject([]);
  loanData$ =  this.loanDataSubject$.asObservable();
  dealDataSubject$: BehaviorSubject<IDealData[]> = new BehaviorSubject([]);
  dealData$ =  this.dealDataSubject$.asObservable();
  loanNumbersSubject$: BehaviorSubject<any[]> = new BehaviorSubject([]);
  loanNumbers$ =  this.loanNumbersSubject$.asObservable();
  showInactiveLoans: boolean = false;
  activeLoanCheckboxText: string = "Show Inactive Loans";
  activeDealCheckboxText: string = "Show Inactive Deals";
  constructor(
    protected readonly taskDataService: TaskDataService,
    protected readonly featureFlagsService: FeatureFlagsService
  ) {
     this.onShowInactiveLoansChanged = this.onShowInactiveLoansChanged.bind(this);
     this.onShowInactiveDealsChanged = this.onShowInactiveDealsChanged.bind(this);
     this.loanData$.subscribe((data) => {
       this.loanData = data;
     });
     this.dealData$.subscribe((data) => {
       this.dealData = data;
     });
     this.loanNumbers$.subscribe((data) => {
       this.loanNumbers = data;
     });
   }

  async ngOnInit() {
    const loanNumberData = await lastValueFrom(this.taskDataService.getLoanNumbersWithDeal());
    // Store loan data with original structure for proper binding
    this.allLoanData = loanNumberData[1];
    // Store original data for filtering
    this.activeLoanData = filterLoanData(this.allLoanData, false);
    this.loanData = this.activeLoanData; // Default to show only active loans
    // Emit the loaded data so subscribers are notified - filtered distinct by loan name
    // to avoid showing duplicate entries for loans with multiple borrowers
    const distinctActiveLoanData = Array.from(
      new Map(this.activeLoanData.map(loan => [loan.name, loan])).values()
    );
    this.loanDataSubject$.next(distinctActiveLoanData);


    // Deals and LoanNumbers are US-only concepts; EU deployments have no deal/US-loan data to fetch
    if (!this.featureFlagsService.isEu()) {
      const dealData = await lastValueFrom(this.taskDataService.getDeals());
      this.allDealData = dealData[1];
      this.activeDealData = filterDealData(this.allDealData, false);
      this.dealData = this.activeDealData; // Default to show only active deals
      // Emit the loaded data so subscribers are notified
      this.dealDataSubject$.next(this.activeDealData);

      const loanNumbers = await lastValueFrom(this.taskDataService.getLoanNumbers());
      this.allLoanNumbers = loanNumbers[1];
      this.activeLoanNumbers = this.allLoanNumbers.filter((ln: any) => ln.status === "Active");
      this.loanNumbers = this.activeLoanNumbers; // Default to show only active loan numbers
      this.loanNumbersSubject$.next(this.loanNumbers);
    }
  }



  onShowInactiveLoansChanged(e: any): void {
    this.showInactiveLoans = e.value;
    // Check if loan data is initialized
    if (!this.allLoanData || !Array.isArray(this.allLoanData)) {
      return;
    }
    setTimeout(() => {
      if (e.value) {
        // Show all loans (active and inactive) - deduplicated by loan name
        const distinctAllLoanData = Array.from(
          new Map(this.allLoanData.map(loan => [loan.name, loan])).values()
        );
        this.loanDataSubject$.next(distinctAllLoanData);
        this.loanNumbersSubject$.next(this.allLoanNumbers);
      } else {
      // Show only active loans - use pre-cached filtered array, deduplicated
      const distinctActiveLoanData = Array.from(
        new Map(this.activeLoanData.map(loan => [loan.name, loan])).values()
      );
      this.loanDataSubject$.next(distinctActiveLoanData);
      this.loanNumbersSubject$.next(this.activeLoanNumbers);
    }
    },10);
  }

    onShowInactiveDealsChanged(e: any): void {
    // Check if deal data is initialized
    if (!this.allDealData || !Array.isArray(this.allDealData)) {
      return;
    }

    setTimeout(() => {
      if (e.value) {
        // Show all deals (active and inactive)
        this.dealDataSubject$.next(this.allDealData);
      } else {
      // Show only active deals - use pre-cached filtered array
      this.dealDataSubject$.next(this.activeDealData);
    }
    },10);
  }

  // Custom item template function for loan dropdown
  loanItemTemplate = (itemData: any, itemIndex: number, itemElement: any) => {
    const cssClass = itemData.status === 'Inactive' ? 'inactive-item' : 'active-item';
    const displayText = `${itemData.name} (${itemData.status})`;
    UtilitiesService.renderText(itemElement, displayText, { className: cssClass });
    return itemElement;
  }

  dealItemTemplate = (itemData: any, itemIndex: number, itemElement: any) => {
    const cssClass = itemData.isActive === false ? 'inactive-item' : 'active-item';
    const displayText = UtilitiesService.formatDealDisplay(itemData);
    UtilitiesService.renderText(itemElement, displayText, { className: cssClass });
    return itemElement;
  }

  // Custom cell template function for data grid loan columns
  loanCellTemplate = (container: any, options: any, loanDataSource?: any[]) => {
    const dataSource = loanDataSource || this.loanData;

    // For lookup columns, options.data contains the row data and options.value is the raw field value
    // For regular columns, we need to find the loan by the field value
    let loanNumber = options.value;

    // If this is part of a row object, get the loan number from the data
    if (options.data?.loanNumber) {
      loanNumber = options.data.loanNumber;
    }

    const loan = dataSource.find(l => l.name === loanNumber);
    if (loan) {
      const cssClass = loan.status === 'Inactive' ? 'inactive-item' : 'active-item';
      const displayText = `${loan.name} (${loan.status})`;
      UtilitiesService.renderText(container, displayText, { className: cssClass });
    } else {
      UtilitiesService.renderPlainText(container, loanNumber || '');
    }
  }


  // Custom cell template function for data grid deal columns
  dealCellTemplate = (container: any, options: any, dealDataSource?: any[]) => {
    const dataSource = dealDataSource || this.dealData;

    // For lookup columns, options.data contains the row data and options.value is the raw field value
    // For regular columns, we need to find the deal by the field value
    let dealNumber = options.value;

    // If this is part of a row object, get the deal number from the data
    if (options.data?.dealNumber) {
      dealNumber = options.data.dealNumber;
    }

    const deal = dataSource?.find(l => l.name === dealNumber || l.dealNumber === dealNumber);
    if (deal) {
      const cssClass = deal.isActive === false ? 'inactive-item' : 'active-item';
      const displayText = UtilitiesService.formatDealDisplay(deal);
      UtilitiesService.renderText(container, displayText, { className: cssClass });
    } else if (options.data && (options.data.dealName || options.data.dealNumber || options.data.name)) {
      const displayText = UtilitiesService.formatDealDisplay(options.data);
      UtilitiesService.renderText(container, displayText);
    } else {
      UtilitiesService.renderPlainText(container, dealNumber || '');
    }
  }

  protected resolveDealNameForLoanNumber(loanNumber?: string | null): string | undefined {
    if (!loanNumber) {
      return undefined;
    }

    const normalizedLoanNumber = loanNumber.trim();
    const loanSources = [this.allLoanData, this.loanData, this.allLoanNumbers, this.loanNumbers];

    for (const source of loanSources) {
      if (!Array.isArray(source)) {
        continue;
      }

      const matchingLoan = source.find((loan: any) => loan?.name === normalizedLoanNumber || loan?.loanNumber === normalizedLoanNumber);
      if (!matchingLoan) {
        continue;
      }

      if (matchingLoan.dealName) {
        return this.normalizeDealName(matchingLoan.dealName);
      }

      if (matchingLoan.dealNumber) {
        return this.normalizeDealName(matchingLoan.dealNumber);
      }
    }

    return undefined;
  }

  protected appendDealNameToSubmission<T extends { dealName?: string; loanNumber?: string; loanNumbers?: any[]; loanAmounts?: any[] }>(submissionData: T): T {
    if (!submissionData || submissionData.dealName) {
      return submissionData;
    }

    const selectedLoanNumber = this.getPrimaryLoanNumber(submissionData);
    const dealName = this.resolveDealNameForLoanNumber(selectedLoanNumber);

    if (!dealName) {
      return submissionData;
    }

    return {
      ...submissionData,
      dealName,
    };
  }

  protected getPrimaryLoanNumber(submissionData: { loanNumber?: string; loanNumbers?: any[]; loanAmounts?: any[] }): string | undefined {
    if (submissionData.loanNumber) {
      return submissionData.loanNumber;
    }

    const firstLoanNumber = submissionData.loanNumbers?.[0];
    if (typeof firstLoanNumber === 'string') {
      return firstLoanNumber;
    }

    if (firstLoanNumber?.loanNumber) {
      return firstLoanNumber.loanNumber;
    }

    const firstLoanAmount = submissionData.loanAmounts?.[0];
    if (typeof firstLoanAmount === 'string') {
      return firstLoanAmount;
    }

    return firstLoanAmount?.loanNumber;
  }

  private normalizeDealName(dealIdentifier?: string | null): string | undefined {
    if (!dealIdentifier) {
      return undefined;
    }

    const normalizedDealIdentifier = dealIdentifier.trim();
    const dealSources = [this.allDealData, this.dealData];

    for (const source of dealSources) {
      if (!Array.isArray(source)) {
        continue;
      }

      const matchingDeal = source.find((deal: any) => deal?.name === normalizedDealIdentifier || deal?.dealNumber === normalizedDealIdentifier);
      if (matchingDeal?.name) {
        return matchingDeal.name;
      }
    }

    return normalizedDealIdentifier;
  }

}
